import { useEffect, useState } from "react";
import api from "../../services/api";
import AddressAutocompleteInput from "../AddressAutocompleteInput";
import { camposDomicilioContactoDesdePlaces } from "../../utils/googlePlacesAddressLine";
import { PROVINCIAS_ES, provinciaIdPorNombre } from "../../utils/provinciasEs";
import { cpPorPoblacionId, cpConflictoConOtraPoblacion } from "../../utils/poblacionesFavoritas";
import SelectPaisDomicilio from "./SelectPaisDomicilio";

const PARTES = [
  { key: "numero", label: "Número", placeholder: "Nº" },
  { key: "bloque", label: "Bloque", placeholder: "Bloque" },
  { key: "escalera", label: "Escalera", placeholder: "Esc." },
  { key: "piso", label: "Altura", placeholder: "Piso" },
  { key: "puerta", label: "Puerta", placeholder: "Puerta" },
];

/**
 * Bloque de domicilio de propietario/contacto (misma descomposición que la APP):
 * Dirección → Número/Bloque/Escalera/Altura/Puerta → CP/Población/Provincia/País.
 */
export default function CamposDomicilioContacto({
  form,
  setForm,
  inpSt,
  labelSt,
  isDark,
  disabled = false,
  placesLocalityContext = null,
}) {
  const [poblaciones, setPoblaciones] = useState([]);

  useEffect(() => {
    const provId = provinciaIdPorNombre(form.provincia);
    if (!provId) {
      setPoblaciones([]);
      return undefined;
    }
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/auxiliares/poblaciones/${provId}`);
        if (alive) setPoblaciones(data?.data || []);
      } catch {
        if (alive) setPoblaciones([]);
      }
    })();
    return () => { alive = false; };
  }, [form.provincia]);

  const patch = (partial) => setForm((f) => ({ ...f, ...partial }));
  const tightInp = { ...inpSt, padding: "8px 6px" };

  return (
    <div
      data-domicilio-contacto="desglosado"
      style={{ gridColumn: "span 2", display: "flex", flexDirection: "column", gap: 12 }}
    >
      <div>
        <label style={labelSt}>Dirección</label>
        <AddressAutocompleteInput
          value={form.direccion || ""}
          onValueChange={(v) => patch({ direccion: v })}
          onPlaceAddressResolved={(parts) => setForm((f) => ({
            ...f,
            ...camposDomicilioContactoDesdePlaces(parts, f),
          }))}
          style={inpSt}
          disabled={disabled}
          placesLocalityContext={placesLocalityContext}
        />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(0, 1fr))", gap: 8 }}>
        {PARTES.map(({ key, label, placeholder }) => (
          <div key={key} style={{ minWidth: 0 }}>
            <label style={labelSt}>{label}</label>
            <input
              value={form[key] || ""}
              onChange={(e) => patch({ [key]: e.target.value })}
              disabled={disabled}
              placeholder={placeholder}
              style={{ ...tightInp, opacity: disabled ? 0.6 : 1, cursor: disabled ? "not-allowed" : "text" }}
              autoComplete="off"
            />
          </div>
        ))}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "4.7rem minmax(0, 1.45fr) minmax(0, 1.15fr) 5.4rem",
          gap: 8,
          alignItems: "end",
        }}
      >
        <div style={{ minWidth: 0 }}>
          <label style={labelSt}>C.P.</label>
          <input
            value={form.codigo_postal || ""}
            onChange={(e) => patch({ codigo_postal: e.target.value.replace(/\D/g, "").slice(0, 5) })}
            disabled={disabled}
            inputMode="numeric"
            maxLength={5}
            placeholder="03700"
            style={{
              ...tightInp,
              textAlign: "center",
              letterSpacing: "0.06em",
              opacity: disabled ? 0.6 : 1,
              cursor: disabled ? "not-allowed" : "text",
            }}
            autoComplete="off"
          />
        </div>
        <div style={{ minWidth: 0 }}>
          <label style={labelSt}>Población</label>
          {poblaciones.length > 0 ? (
            <select
              value={form.poblacion || ""}
              onChange={(e) => {
                const nombre = e.target.value;
                const row = poblaciones.find((p) => p.nombre === nombre);
                setForm((f) => {
                  const next = { ...f, poblacion: nombre };
                  const cp = row ? cpPorPoblacionId(row.id) : "";
                  if (cp) {
                    next.codigo_postal = cp;
                  } else if (row && cpConflictoConOtraPoblacion(f.codigo_postal, row.id)) {
                    // No dejar CP de otra localidad (p. ej. 03700 Dénia con Elx/Elche).
                    next.codigo_postal = "";
                  }
                  return next;
                });
              }}
              disabled={disabled}
              style={{ ...inpSt, opacity: disabled ? 0.6 : 1 }}
            >
              <option value="">— Población</option>
              {poblaciones.map((p) => (
                <option key={p.id} value={p.nombre}>{p.nombre}</option>
              ))}
            </select>
          ) : (
            <input
              value={form.poblacion || ""}
              onChange={(e) => patch({ poblacion: e.target.value })}
              disabled={disabled}
              style={{ ...inpSt, opacity: disabled ? 0.6 : 1, cursor: disabled ? "not-allowed" : "text" }}
              autoComplete="off"
            />
          )}
        </div>
        <div style={{ minWidth: 0 }}>
          <label style={labelSt}>Provincia</label>
          <select
            value={form.provincia || ""}
            onChange={(e) => patch({ provincia: e.target.value })}
            disabled={disabled}
            style={{ ...inpSt, opacity: disabled ? 0.6 : 1 }}
          >
            <option value="">— Provincia</option>
            {PROVINCIAS_ES.map((p) => (
              <option key={p.id} value={p.nombre}>{p.nombre}</option>
            ))}
          </select>
        </div>
        <div style={{ minWidth: 0 }}>
          <label style={labelSt}>País</label>
          <SelectPaisDomicilio
            value={form.pais || "ES"}
            onChange={(code) => patch({ pais: code })}
            disabled={disabled}
            inpSt={tightInp}
            isDark={isDark}
          />
        </div>
      </div>
    </div>
  );
}
